import { PlusCircleIcon, UserCircleIcon, CameraIcon } from '@heroicons/react/16/solid'
import StaffNav from './managernav'
import { PhoneIcon, LifebuoyIcon, NewspaperIcon, MapIcon } from '@heroicons/react/20/solid'
import { useEffect, useState, useContext, useRef } from 'react'
import { AuthContext } from '../AuthContext'
import MoreInfo from './moreinfo'
import { useNavigate } from 'react-router-dom'
import MapLogin from '../manager/maplogin'
import { getProfilePicSrc } from '../utils/mediaUrl'

export default function Users() {
  const user = useContext(AuthContext)
  const [people, setPeople] = useState([])
  const [company, setCompany] = useState([])
  const [information, setInformation] = useState(null)
  const [showMap, setShowMap] = useState(false)
  const [showCamera, setShowCamera] = useState(false)
  const [cameraPerson, setCameraPerson] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [userId, setUser] = useState(null)
  const [accessLevel, setAccessLevel] = useState(null)
  const [newAccessLevel, setNewAccessLevel] = useState({ id: null, access: 'staff' })

  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const navigate = useNavigate()

  const normalizeDateKey = (value) => {
    if (!value) return null

    if (typeof value === 'string') {
      const directMatch = value.match(/(\d{4}-\d{2}-\d{2})/)
      if (directMatch) return directMatch[1]
    }

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return null

    const year = parsed.getFullYear()
    const month = String(parsed.getMonth() + 1).padStart(2, '0')
    const day = String(parsed.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const todayKey = normalizeDateKey(new Date())

  const isLeavingToday = (person) => {
    const leavingDateRaw = person?.LeavingDate ?? person?.leavingDate ?? null
    const leavingDateKey = normalizeDateKey(leavingDateRaw)
    return leavingDateKey === todayKey
  }

  const formatDateLabel = (dateKey) => {
    if (!dateKey) return null
    const [year, month, day] = dateKey.split('-').map(Number)
    if (!year || !month || !day) return dateKey
    const localDate = new Date(year, month - 1, day)
    return localDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

    const fetchStaff = async () => {
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/staff`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        })

        const companyRes = await fetch(`https://api.insightos.co.uk/api/company`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        })

        if (companyRes.ok) {
          const companyData = await companyRes.json()
          setCompany(companyData.data)
        }

        if (response.ok) {
          const staffData = await response.json()
          const processedStaff = staffData.data.map((person) => ({
            ...person,
            ProfilePic: getProfilePicSrc(person),
          }))
          setPeople(processedStaff)
        }
      } catch (error) {
        console.error('Error fetching staff or company data:', error)
      }
    }

    useEffect(() => {
    fetchStaff()
  }, [user])

  const mypic = async (file) => {
    const formData = new FormData()
    formData.append("file", file)
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/adminpic/${user.id}`, {
        method: "PUT",
        body: formData,
      })
      if (response.ok) {
        window.location.reload()
      } else {
        console.error("Failed to upload file", response.statusText)
      }
    } catch (error) {
      console.error("Error uploading file:", error)
    }
  }

  // Camera logic
  const openCamera = (person) => {
    setCameraPerson(person)
    setShowCamera(true)
    setTimeout(() => {
      if (videoRef.current) {
        navigator.mediaDevices.getUserMedia({ video: true })
          .then(stream => {
            videoRef.current.srcObject = stream
            videoRef.current.play()
          })
          .catch(err => {
            alert("Could not access camera.")
            setShowCamera(false)
          })
      }
    }, 100)
  }

  const closeCamera = () => {
    setShowCamera(false)
    setCameraPerson(null)
    if (videoRef.current && videoRef.current.srcObject) {
      let tracks = videoRef.current.srcObject.getTracks()
      tracks.forEach(track => track.stop())
      videoRef.current.srcObject = null
    }
  }

  const takePicture = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d')
      context.drawImage(videoRef.current, 0, 0, 320, 240)
      canvasRef.current.toBlob(blob => {
        if (blob) {
          mypic(blob)
          closeCamera()
        }
      }, 'image/jpeg')
    }
  }

  if (information) {
    return <MoreInfo person={information} />
  }

  if (showMap) {
    return (<MapLogin selectedLocation={showMap} />)
  }

  const handleDeleteUser = async (id) => {
    const response = await fetch(`https://api.insightos.co.uk/api/staffcsv/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
  
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user_data_${id}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setUser(id)
      setDeleteModal(true)
  
      console.log('User deleted and CSV downloaded');
    } else {
      console.error('Failed to delete user and get CSV:', response.statusText);
    }
  };

  const handleDelete = async () => {

    try {
      const response = await fetch(`https://api.insightos.co.uk/api/deletestaff/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      })

      if (response.ok) {
        setDeleteModal(false)
        window.location.reload()
      } else {
        console.error('Failed to delete user:', response.statusText)
      }
    }
    catch (error) {
      console.error('Error deleting user:', error)
    }
  }

  const disableAccount = async (personId) => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/disableaccount/${personId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      })

      if (response.ok) {
        console.log('Account status changed successfully')
        // refresh the people list
        await fetchStaff()
      } else {
        console.error('Failed to change account status:', response.statusText)
      }
    } catch (err) {
      console.error('Error changing account status:', err)
    }
  }

  return (
    <div data-component="manager-users-admin">
      <div className="border-b mb-5 border-gray-200 pb-5 sm:flex sm:items-center sm:justify-between pr-3 pl-3">
        <h3 className="text-base font-semibold text-gray-900">Users</h3>
        {company.map((company) => (
          <div key={company.CompanyCode} className="flex items-center gap-x-4">
            <button
             data-component="manager-users-admin-action"
             data-feature="upgrade-plan"
             onClick={() => navigate('/billing')}
             type="button" className="inline-flex items-center mt-5 rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              <PlusCircleIcon className="h-5 w-5 mr-2 text-white-500" aria-hidden="true" />
              {company.Plan} - Upgrade Plan
            </button>
            <p className="inline-flex items-center mt-5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              <UserCircleIcon className="h-5 w-5 mr-2 text-white-500" aria-hidden="true" />
              {people.length} / {company.Users} Users
            </p>
            <button
              data-component="manager-users-admin-action"
              data-feature="view-geo"
              onClick={() => setShowMap(company.GeoTag)}
              type="button"
              className="inline-flex items-center mt-5 rounded-md px-3 py-2 text-sm font-semibold text-yellow-300 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600" >
              <MapIcon className="h-5 w-5 mr-2 text-emerald-500" aria-hidden="true" />
            </button>
          </div>
        ))}
        {people.length !== company.Users && (
          <div className="mt-3 sm:ml-4 sm:mt-0">
            <button
              data-component="manager-users-admin-action"
              data-feature="create-user"
              onClick={() => navigate('/newuser')}
              type="button"
              className="inline-flex items-center mt-5 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Create New User
            </button>
          </div>
        )}
      </div>
{deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center">
            <h2 className="text-lg font-semibold">User Deleted</h2>
            <p className="mt-2 text-sm text-gray-600">
  A CSV file containing this user’s data has been downloaded to your computer. 
  By clicking the delete button, all data for this user will be permanently removed from the system. 
  Are you sure you want to continue?
</p>
            <button
              data-component="manager-users-admin-action"
              data-feature="confirm-delete"
              onClick={handleDelete}
              className="mt-4 rounded bg-gray-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
            >
              Delete
            </button>
            <button
              data-component="manager-users-admin-action"
              data-feature="cancel-delete"
              onClick={() => setDeleteModal(false)}
              className="mt-2 rounded bg-red-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <ul
        role="list"
        className="divide-y divide-gray-100 ml-3 mr-3 mt-2 "
      >
        {people.map((person) => {
          const leavingDateKey = normalizeDateKey(person?.LeavingDate ?? person?.leavingDate ?? null)
          const leavingToday = isLeavingToday(person)
          return (
          <li
            key={person.Email}
            className={`flex flex-col sm:flex-row sm:justify-between rounded-lg border mt-2 shadow-sm pl-1 pr-1 gap-x-6 py-5 ${
              person.Email === user.email ? "bg-green-100 border-green-500" : "bg-blue-100 border-blue-700"
            }`}
          >
            <div className="flex min-w-0 gap-x-4 ml-2 mr-2">
              <div className="relative">
                <img
                  alt=""
                  src={person.ProfilePic}
                  className="size-12 flex-none rounded-full bg-gray-50 hover:bg-gray-900 cursor-pointer"
                />
                {person.Email === user.email && (
                  <div className="flex flex-col gap-1 mt-2">
                    <div className="relative">
                      <button
                        data-component="manager-users-admin-action"
                        data-feature="upload-avatar"
                        type="button"
                        className="rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        style={{ position: "relative", zIndex: 1 }}
                      >
                        Change
                        <input
                          type="file"
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          style={{ zIndex: 2 }}
                          onChange={(e) => {
                            const file = e.target.files[0]
                            if (file) {
                              mypic(file)
                            }
                          }}
                        />
                      </button>
                    </div>
                    <button
                      data-component="manager-users-admin-action"
                      data-feature="open-camera"
                      type="button"
                      onClick={() => openCamera(person)}
                      className="rounded bg-yellow-500 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-yellow-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 flex items-center gap-1"
                      style={{ position: "relative", zIndex: 1 }}
                    >
                      <CameraIcon className="h-4 w-4" />
                      Take Picture
                    </button>
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-auto">
                <p className={`text-sm/6 font-semibold ${leavingToday ? 'text-red-600' : 'text-gray-900'}`}>{person.Name}</p>
                <p className="mt-1 truncate text-xs/5 text-black">{person.Email}</p>
                {person.EmailVerified === 0 && (
                  <p className="text-xs/5 text-red-600 font-semibold">Email is Not Verified</p>
                )}
                <p className="mt-1 text-xs/5 text-black">{person.Role}</p>
                {leavingToday && (
                  <p className="mt-1 text-xs font-semibold text-red-600">
                    Disable account after shift or the users account will be disabled overnight.
                  </p>
                )}
              </div>
            </div>
            <div className="mt-3 w-full px-2 sm:hidden">
              <div className="rounded-xl border border-indigo-200 bg-white/70 p-2">
                <p className="mb-2 text-xs font-semibold text-blue-700">Access: {person.Access}</p>
                {leavingDateKey && (
                  <p className="mb-2 text-xs text-gray-700">Leaving on {formatDateLabel(leavingDateKey)}</p>
                )}
                {person.EmailError === 1 && (
                  <p className="mb-2 text-xs font-semibold text-red-600">
                    There was an error sending this user email. Please update their email address.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-component="manager-users-admin-action"
                    data-feature={`more-info-mobile-${person.id}`}
                    onClick={() => setInformation(person)}
                    type="button"
                    className="rounded-lg bg-indigo-600 px-2 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
                  >
                    More Info
                  </button>
                  {user.access === "admin" && (
                    <button
                      data-component="manager-users-admin-disable-account"
                      data-feature={`disable-account-mobile-${person.id}`}
                      onClick={() => disableAccount(person.id)}
                      type="button"
                      className="rounded-lg bg-orange-500 px-2 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-orange-600"
                    >
                      {Number(person.DisableAccount) === 1 ? 'Enable Account' : 'Disable Account'}
                    </button>
                  )}

                  {user.access === "admin" && (
                    <button
                      data-component="manager-users-admin-action"
                      data-feature={`delete-user-mobile-${person.id}`}
                      onClick={() => handleDeleteUser(person.id)}
                      type="button"
                      className="rounded-lg bg-red-500 px-2 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-600"
                    >
                      Delete User
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end">
              <p className="text-sm/6 text-gray-900">{person.Role}</p>
              {leavingDateKey && (
                <p className="mt-1 text-xs/5 text-gray-700 mb-1">
                  Leaving on {formatDateLabel(leavingDateKey)}
                </p>
              )}
              <p className="mt-1 text-xs/5 text-blue-700 mb-1">Access: {person.Access}</p>
              {person.EmailError === 1 && (
                <p className="text-xs/5 text-red-600 font-semibold mb-1">There was an error sending this user email. Please update their email address.</p>
              )}
              <button
                data-component="manager-users-admin-action"
                data-feature={`more-info-${person.id}`}
                onClick={() => setInformation(person)}
                type="button"
                className="rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                More Information
              </button>

            {user.access === "admin" && (
              <button
                data-component="manager-users-admin-disable-account"
                data-feature={`disable-account-${person.id}`}
                onClick={() => disableAccount(person.id)}
                type="button"
                className="rounded bg-orange-500 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-orange-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 mt-1"
              >
                {Number(person.DisableAccount) === 1 ? 'Enable Account' : 'Disable Account'}
              </button>
            )}


                 {user.access === "admin" && (
                <button
                  data-component="manager-users-admin-action"
                  data-feature={`delete-user-${person.id}`}
                  onClick={() => handleDeleteUser(person.id)}
                  type="button"
                  className="rounded bg-red-500 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 mt-1"
                >
                  Delete User
                </button>

              )}
            </div>
          </li>
        )})}
      </ul>
     
      {/* Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center">
            <video ref={videoRef} width={320} height={240} autoPlay className="rounded border mb-2" />
            <canvas ref={canvasRef} width={320} height={240} style={{ display: 'none' }} />
            <div className="flex gap-2 mt-2">
              <button
                data-component="manager-users-admin-action"
                data-feature="capture-photo"
                onClick={takePicture}
                className="rounded bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700"
              >
                Capture
              </button>
              <button
                data-component="manager-users-admin-action"
                data-feature="cancel-camera"
                onClick={closeCamera}
                className="rounded bg-gray-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}